import { useState, useEffect, useRef, useCallback } from 'react';
import logger from '../../lib/logger';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Html5Qrcode } from 'html5-qrcode';
import { 
  QrCode, 
  Camera, 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  Users,
  Clock,
  Ticket,
  Search,
  RefreshCw,
  CameraOff,
  Smartphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const CheckinPage = () => {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Camera states
  const [cameraActive, setCameraActive] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [cameraError, setCameraError] = useState(null);
  const [scanning, setScanning] = useState(false);
  
  // Refs
  const scannerRef = useRef(null);
  const lastScannedToken = useRef(null);
  const debounceTimeout = useRef(null);

  useEffect(() => {
    fetchStats();
    
    // Check URL for token param
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      handleLookup(tokenParam);
    }
    
    // Get available cameras
    Html5Qrcode.getCameras().then(devices => {
      if (devices && devices.length > 0) {
        setCameras(devices);
        // Prefer back camera
        const backCamera = devices.find(d => 
          d.label.toLowerCase().includes('back') || 
          d.label.toLowerCase().includes('traseira') ||
          d.label.toLowerCase().includes('rear')
        );
        setSelectedCamera(backCamera?.id || devices[0].id);
      }
    }).catch(err => {
      logger.error('Error getting cameras:', err);
    });
    
    return () => {
      stopCamera();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/checkin/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      logger.error('Error fetching stats:', error);
    }
  };

  const extractToken = (qrData) => {
    // If it's a URL with token param, extract it
    if (qrData.includes('token=')) {
      try {
        const url = new URL(qrData);
        return url.searchParams.get('token') || qrData;
      } catch {
        // Try regex as fallback
        const match = qrData.match(/token=([^&]+)/);
        return match ? match[1] : qrData;
      }
    }
    // Otherwise use the raw value
    return qrData;
  };

  const handleQrCodeSuccess = useCallback((decodedText) => {
    const extractedToken = extractToken(decodedText);
    
    // Debounce: skip if same token scanned recently
    if (lastScannedToken.current === extractedToken) {
      return;
    }
    
    // Set debounce
    lastScannedToken.current = extractedToken;
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      lastScannedToken.current = null;
    }, 2000);
    
    // Vibrate if available
    if (navigator.vibrate) {
      navigator.vibrate(200);
    }
    
    // Update token and lookup
    setToken(extractedToken);
    handleLookup(extractedToken);
    
    // Pause scanning briefly
    setScanning(false);
    setTimeout(() => setScanning(true), 2000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCamera = async () => {
    if (!selectedCamera) {
      toast.error('Nenhuma câmera disponível');
      return;
    }

    setCameraError(null);
    setResult(null);

    try {
      const scanner = new Html5Qrcode('qr-reader');
      scannerRef.current = scanner;
      
      await scanner.start(
        selectedCamera,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        handleQrCodeSuccess,
        () => {} // Ignore scan errors
      );
      
      setCameraActive(true);
      setScanning(true);
      toast.success('Câmera iniciada. Aponte para o QR Code.');
    } catch (err) {
      logger.error('Camera start error:', err);
      setCameraError(
        err.toString().includes('Permission') || err.toString().includes('NotAllowed')
          ? 'Permissão de câmera negada. Por favor, permita o acesso à câmera nas configurações do navegador.'
          : 'Erro ao iniciar a câmera. Verifique se outra aplicação está usando a câmera.'
      );
      toast.error('Erro ao iniciar câmera');
    }
  };

  const stopCamera = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current = null;
      } catch (err) {
        logger.error('Error stopping camera:', err);
      }
    }
    setCameraActive(false);
    setScanning(false);
  };

  const handleLookup = async (lookupToken = token) => {
    if (!lookupToken.trim()) {
      toast.error('Informe o token do QR Code');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${API_URL}/api/admin/checkin/lookup?token=${encodeURIComponent(lookupToken)}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setResult({ type: 'lookup', data, token: lookupToken });
      } else if (response.status === 404) {
        setResult({ type: 'not_found' });
        toast.error('Participante não encontrado');
      } else {
        throw new Error('Erro ao buscar participante');
      }
    } catch (error) {
      logger.error('Lookup error:', error);
      toast.error('Erro ao buscar participante');
      setResult({ type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCheckin = async () => {
    const currentToken = result?.token || token;
    if (!currentToken.trim()) return;

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/checkin/confirm`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: currentToken.trim() })
      });

      const data = await response.json();

      if (response.ok) {
        if (data.status === 'already_checked_in') {
          setResult({ type: 'already_checked_in', data });
          toast.warning('Participante já validado anteriormente');
        } else {
          setResult({ type: 'success', data });
          toast.success('Check-in realizado com sucesso!');
          // Vibrate for success
          if (navigator.vibrate) {
            navigator.vibrate([100, 50, 100]);
          }
          fetchStats();
        }
      } else if (response.status === 404) {
        setResult({ type: 'not_found' });
        toast.error('Participante não encontrado');
      } else if (response.status === 400) {
        setResult({ type: 'payment_pending', message: data.detail });
        toast.error(data.detail);
      } else {
        throw new Error('Erro ao realizar check-in');
      }
    } catch (error) {
      logger.error('Checkin error:', error);
      toast.error('Erro ao realizar check-in');
      setResult({ type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setToken('');
    setResult(null);
    lastScannedToken.current = null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const ResultDisplay = () => {
    if (!result) return null;

    switch (result.type) {
      case 'success':
        return (
          <Card className="bg-gray-500/10 border-gray-500/30 animate-in fade-in zoom-in duration-300">
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-400 mb-2">Check-in Realizado!</h3>
              <div className="space-y-2 text-white">
                <p className="text-lg font-medium">{result.data.nome_responsavel}</p>
                <p className="text-gray-400">&</p>
                <p className="text-lg font-medium">{result.data.nome_conjuge}</p>
                <p className="text-sm text-gray-400 mt-4">
                  Código: <span className="font-mono text-kairos-orange">{result.data.codigo_ingresso}</span>
                </p>
              </div>
            </CardContent>
          </Card>
        );

      case 'already_checked_in':
        return (
          <Card className="bg-yellow-500/10 border-yellow-500/30 animate-in fade-in zoom-in duration-300">
            <CardContent className="p-6 text-center">
              <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-yellow-400 mb-2">Já Validado</h3>
              <div className="space-y-2 text-white">
                <p className="text-lg font-medium">{result.data.nome_responsavel}</p>
                <p className="text-gray-400">&</p>
                <p className="text-lg font-medium">{result.data.nome_conjuge}</p>
                <p className="text-sm text-gray-400 mt-4">
                  Check-in em: {formatDate(result.data.checkin_em)}
                </p>
                <p className="text-xs text-gray-500">
                  Por: {result.data.checkin_por}
                </p>
              </div>
            </CardContent>
          </Card>
        );

      case 'lookup':
        return (
          <Card className="bg-blue-500/10 border-blue-500/30 animate-in fade-in zoom-in duration-300">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-8 h-8 text-blue-400" />
                <div>
                  <h3 className="text-xl font-bold text-white">{result.data.nome_responsavel}</h3>
                  <p className="text-gray-400">& {result.data.nome_conjuge}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-3 bg-obsidian-50 rounded-lg">
                  <p className="text-gray-500 text-xs">Código Ingresso</p>
                  <p className="font-mono text-kairos-orange">{result.data.codigo_ingresso || '-'}</p>
                </div>
                <div className="p-3 bg-obsidian-50 rounded-lg">
                  <p className="text-gray-500 text-xs">Pagamento</p>
                  <p className={result.data.status_pagamento === 'aprovado' ? 'text-gray-400' : 'text-yellow-400'}>
                    {result.data.status_pagamento === 'aprovado' ? 'Aprovado' : 'Pendente'}
                  </p>
                </div>
                <div className="p-3 bg-obsidian-50 rounded-lg">
                  <p className="text-gray-500 text-xs">Check-in</p>
                  <p className={result.data.checkin_status === 'presente' ? 'text-gray-400' : 'text-gray-400'}>
                    {result.data.checkin_status === 'presente' ? 'Presente' : 'Não chegou'}
                  </p>
                </div>
                <div className="p-3 bg-obsidian-50 rounded-lg">
                  <p className="text-gray-500 text-xs">Evento</p>
                  <p className="text-white text-sm truncate">{result.data.evento_nome}</p>
                </div>
              </div>

              {result.data.status_pagamento === 'aprovado' && result.data.checkin_status !== 'presente' && (
                <Button
                  onClick={handleConfirmCheckin}
                  disabled={loading}
                  className="w-full bg-gray-800 hover:bg-green-600 text-white h-12"
                  data-testid="confirm-checkin-btn"
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Confirmar Check-in
                    </>
                  )}
                </Button>
              )}

              {result.data.status_pagamento !== 'aprovado' && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
                  <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-red-400 font-medium">Pagamento não aprovado</p>
                  <p className="text-gray-500 text-sm">Status: {result.data.status_pagamento}</p>
                </div>
              )}

              {result.data.checkin_status === 'presente' && (
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-center">
                  <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                  <p className="text-yellow-400 font-medium">Já validado anteriormente</p>
                  <p className="text-gray-500 text-sm">Check-in em: {formatDate(result.data.checkin_em)}</p>
                </div>
              )}
            </CardContent>
          </Card>
        );

      case 'not_found':
        return (
          <Card className="bg-red-500/10 border-red-500/30 animate-in fade-in zoom-in duration-300">
            <CardContent className="p-6 text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-red-400 mb-2">Não Encontrado</h3>
              <p className="text-gray-400">Token inválido ou participante não existe.</p>
            </CardContent>
          </Card>
        );

      case 'payment_pending':
        return (
          <Card className="bg-red-500/10 border-red-500/30 animate-in fade-in zoom-in duration-300">
            <CardContent className="p-6 text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-red-400 mb-2">Pagamento Pendente</h3>
              <p className="text-gray-400">{result.message}</p>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="checkin-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-white mb-2">Check-in</h1>
            <p className="text-gray-400">Validar entrada de participantes</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-obsidian-200 border-white/10">
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 text-gray-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{stats.total_aprovados}</p>
                <p className="text-gray-500 text-xs">Total Esperado</p>
              </CardContent>
            </Card>
            <Card className="bg-obsidian-200 border-white/10">
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-400">{stats.total_presentes}</p>
                <p className="text-gray-500 text-xs">Presentes</p>
              </CardContent>
            </Card>
            <Card className="bg-obsidian-200 border-white/10">
              <CardContent className="p-4 text-center">
                <Clock className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-yellow-400">{stats.total_nao_chegaram}</p>
                <p className="text-gray-500 text-xs">Não Chegaram</p>
              </CardContent>
            </Card>
            <Card className="bg-obsidian-200 border-white/10">
              <CardContent className="p-4 text-center">
                <Ticket className="w-6 h-6 text-kairos-orange mx-auto mb-2" />
                <p className="text-2xl font-bold text-kairos-orange">{stats.percentual_presenca}%</p>
                <p className="text-gray-500 text-xs">Presença</p>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Scanner / Input */}
          <div className="space-y-6">
            <Card className="bg-obsidian-200 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-kairos-orange" />
                  Scanner de QR Code
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Camera selector */}
                {cameras.length > 1 && (
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-gray-500" />
                    <Select 
                      value={selectedCamera} 
                      onValueChange={setSelectedCamera}
                      disabled={cameraActive}
                    >
                      <SelectTrigger className="flex-1 bg-obsidian-50 border-white/10 text-white">
                        <SelectValue placeholder="Selecionar câmera" />
                      </SelectTrigger>
                      <SelectContent className="bg-obsidian-200 border-white/10">
                        {cameras.map(cam => (
                          <SelectItem 
                            key={cam.id} 
                            value={cam.id}
                            className="text-white hover:bg-white/10"
                          >
                            {cam.label || `Câmera ${cam.id.slice(0,8)}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Camera Preview */}
                <div 
                  id="qr-reader" 
                  className={`relative aspect-square bg-black rounded-lg overflow-hidden ${!cameraActive ? 'hidden' : ''}`}
                  style={{ maxHeight: '350px' }}
                />
                
                {/* Camera placeholder when inactive */}
                {!cameraActive && !cameraError && (
                  <div className="aspect-square bg-obsidian-50 rounded-lg flex flex-col items-center justify-center text-center p-6" style={{ maxHeight: '350px' }}>
                    <Camera className="w-16 h-16 text-gray-600 mb-4" />
                    <p className="text-gray-400 mb-2">Clique em "Iniciar Câmera" para escanear</p>
                    <p className="text-gray-600 text-sm">Use a câmera traseira para melhor leitura</p>
                  </div>
                )}

                {/* Camera error */}
                {cameraError && (
                  <div className="aspect-square bg-red-500/10 border border-red-500/30 rounded-lg flex flex-col items-center justify-center text-center p-6" style={{ maxHeight: '350px' }}>
                    <CameraOff className="w-16 h-16 text-red-500 mb-4" />
                    <p className="text-red-400 font-medium mb-2">Erro de Câmera</p>
                    <p className="text-gray-400 text-sm">{cameraError}</p>
                  </div>
                )}

                {/* Scanning indicator */}
                {cameraActive && scanning && (
                  <div className="flex items-center justify-center gap-2 text-gray-400">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-sm">Escaneando...</span>
                  </div>
                )}

                {/* Camera controls */}
                <div className="flex gap-2">
                  {!cameraActive ? (
                    <Button
                      onClick={startCamera}
                      className="flex-1 bg-kairos-orange hover:bg-kairos-orange-dark text-white"
                      disabled={!selectedCamera && cameras.length === 0}
                      data-testid="start-camera-btn"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Iniciar Câmera
                    </Button>
                  ) : (
                    <Button
                      onClick={stopCamera}
                      variant="outline"
                      className="flex-1 bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10"
                      data-testid="stop-camera-btn"
                    >
                      <CameraOff className="w-4 h-4 mr-2" />
                      Parar Câmera
                    </Button>
                  )}
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-3 bg-obsidian-200 text-gray-500 text-sm">ou digite o token</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Input
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                    placeholder="Cole o token do QR Code aqui..."
                    className="flex-1 bg-obsidian-50 border-white/10 text-white"
                    data-testid="token-input"
                  />
                  <Button
                    onClick={() => handleLookup()}
                    disabled={loading || !token.trim()}
                    className="bg-kairos-orange hover:bg-kairos-orange-dark text-white"
                    data-testid="lookup-btn"
                  >
                    {loading ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <Search className="w-5 h-5" />
                    )}
                  </Button>
                </div>

                {result && (
                  <Button
                    onClick={resetForm}
                    variant="ghost"
                    className="w-full text-gray-400 hover:text-white"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Nova Consulta
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Result */}
          <div>
            {result ? (
              <ResultDisplay />
            ) : (
              <Card className="bg-obsidian-200 border-white/10 h-full min-h-[400px]">
                <CardContent className="p-6 h-full flex flex-col items-center justify-center text-center">
                  <QrCode className="w-16 h-16 text-gray-600 mb-4" />
                  <p className="text-gray-400 mb-2">Escaneie um QR Code ou cole o token</p>
                  <p className="text-gray-600 text-sm">O resultado aparecerá aqui</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CheckinPage;
